import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, BookOpen, FileText, Search, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Research = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const searchPapers = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await axios.post(`${API}/research/papers`, {
        disease: searchQuery,
        category: activeCategory === 'all' ? '' : activeCategory
      }, {
        withCredentials: true
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching research papers:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchPapers();
    }
  };

  const categories = [
    { id: 'all', name: 'All Categories', icon: <BookOpen size={20} /> },
    { id: 'reports', name: 'Reports', icon: <FileText size={20} /> },
    { id: 'articles', name: 'Articles', icon: <FileText size={20} /> },
    { id: 'studies', name: 'Studies', icon: <FileText size={20} /> },
    { id: 'papers', name: 'Research Papers', icon: <FileText size={20} /> },
    { id: 'ongoing', name: 'Ongoing Trials', icon: <FileText size={20} /> }
  ];

  const renderPapers = (papers, categoryName) => {
    if (!papers || papers.length === 0) {
      return (
        <p style={{ color: '#78909c', padding: '1rem' }}>No {categoryName.toLowerCase()} found.</p>
      );
    }

    return papers.map((paper, index) => (
      <div 
        key={index} 
        className="paper-item"
        onClick={() => window.open(paper.link, '_blank')}
        data-testid={`paper-${categoryName}-${index}`}
      >
        <h4 className="paper-title">{paper.title}</h4>
        {paper.authors && <p className="paper-meta">Authors: {paper.authors}</p>}
        {paper.source && <p className="paper-meta">Source: {paper.source}</p>}
        {paper.pubdate && <p className="paper-meta">Published: {paper.pubdate}</p>}
        <div style={{ marginTop: '0.5rem', color: '#0277bd', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          View Full Paper <ExternalLink size={14} />
        </div>
      </div>
    ));
  };

  return (
    <div className="dashboard-container" data-testid="research-page">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-brand">
          <Activity size={28} />
          MedPlexity
        </div>
        <div className="navbar-menu">
          <button 
            className="nav-link" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </nav>

      <div className="content-section">
        <h2 className="section-title" data-testid="page-title">
          <BookOpen size={32} /> Research Hub
        </h2>
        <p style={{ color: '#78909c', marginBottom: '2rem' }}>
          Access research papers, studies, articles, and ongoing clinical trials
        </p>

        {/* Search Bar */}
        <div className="search-bar-container">
          <div className="search-wrapper">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              className="search-input"
              placeholder="Search for disease, condition, or topic..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={searching}
              data-testid="research-search-input"
            />
            <button 
              className="search-btn" 
              onClick={searchPapers}
              disabled={searching}
              data-testid="search-button"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Category Tabs */}
        {results && (
          <div className="diagnosis-tabs" style={{ marginTop: '2rem' }}>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`category-${cat.id}`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {results && (
          <div style={{ marginTop: '2rem' }}>
            {activeCategory === 'all' && results.all_categories ? (
              // Show all categories
              Object.entries(results.all_categories).map(([category, papers]) => (
                <div key={category} className="research-category" data-testid={`category-section-${category}`}>
                  <h3 className="category-title">
                    <FileText size={24} />
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </h3>
                  {renderPapers(papers, category)}
                </div>
              ))
            ) : (
              // Show specific category
              <div className="research-category">
                <h3 className="category-title">
                  <FileText size={24} />
                  {categories.find(c => c.id === activeCategory)?.name}
                </h3>
                {results.results ? (
                  renderPapers(results.results, activeCategory)
                ) : (
                  <p style={{ color: '#78909c', padding: '1rem' }}>No results available.</p>
                )}
              </div>
            )}
          </div>
        )}

        {!results && (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#78909c' }}>
            <BookOpen size={64} color="#b0bec5" />
            <p style={{ marginTop: '1rem', fontSize: '1.1rem' }}>Enter a disease or topic to search research papers</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Research;
